/**
 * The chrome directives (D179): `restart`, `save` and `load` — words about
 * the SOFTWARE, not the world, answered in the terminal's own register.
 *
 * @remarks
 * `never mind` (R34/D114) showed the shape: a directive with no grammar,
 * matched whole against the compiled lexicon by its canonical intent, and
 * answered by the conversation wrapper before the parser ever reads it. No
 * turn is consumed, the scheduler does not tick, and the world does not
 * change — these are conversation management, exactly as withdrawal is.
 * They are intents in the vocabulary all the same, so a pack aliases them
 * the way it aliases any verb and HELP lists them from the same table it
 * lists everything else.
 *
 * **`restart`** asks before it acts. It parks a `confirm` question — the
 * product's first destructive gate — carrying the two answers as chips
 * (`START OVER` in the danger color, `KEEP IT`) and the words that mean
 * them. Going through with it puts the player exactly where BEGIN put them:
 * the genesis state, on the session's own seed, so a replay of
 * `(bundle, seed, commands)` through a restart is still one deterministic
 * walk — the engine needs no host to do it. Mike's ruling (2026-09-15): an
 * in-game act; the player stays in the session; progress goes, preferences
 * stay (they were never in the state). D229 (E16, the play-test): a closed
 * world still hears it — the one directive the R11 terminal gate yields to,
 * because starting over is the only thing left to do after an ending, and
 * "NOTHING FURTHER CHANGES" was a lie about the one thing that could. The
 * typed confirm says how it is answered (`restartAnswers`), and speaks the
 * pack's own words for the act when it wrote them for the title screen's
 * door (`presentation.titleScreen.restart`) and claimed no slot — the same
 * sentence, because it is the same act.
 *
 * **`again`** (D226, E12; `g`, `repeat`) says the player's last words again:
 * the most recent input that was not itself an `again`, read through the
 * whole conversation — a parked question, an answer, a chrome directive,
 * whatever those words would meet if typed now — while the echo and the
 * history keep what the player actually typed, as D37's splice does. A
 * session that has said nothing yet is told so (`nothingToRepeat`). There is
 * no `undo`, and there will not be one (Mike, 2026-10-02: there is no undo
 * in real life).
 *
 * **`save` / `load`** are one directive each whose answer depends on the
 * pack's `manifest.saves` (§2.1: not a directive some packs have and others
 * lack). On `automatic` they say there is nothing to do; on `manual` they
 * ask the host to write or bring back the one slot — the engine cannot
 * touch storage, so the outcome carries a {@link HostRequest} the host
 * honors. Because the words are in the vocabulary, the parse SUCCEEDS and
 * the pack's D27 reaction table is never consulted for them (it is read
 * only on a whole-input parse failure) — which is why the POC's interim
 * `save` / `load` reactions were deleted in the same round this landed.
 *
 * @packageDocumentation
 */

import type { DefaultResponseSlot, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import { foldCharacters } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { PlayerInputOutcome } from './process-player-input.js';

import { type EffectiveSaveMode, effectiveSaveModeOf } from '../bundle/host-declaration.js';
import { bumpResponseUsage, speakSlot, type SpokenResponse } from '../defaults/default-responses.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { terminalEndingOf } from '../narrative/session-ended.js';
import { nextRecordOf } from '../record/record-of-run.js';
import { CHROME_DIRECTIVE_TOKENS, type ChromeDirective } from './chrome-directive-tokens.js';
import { DECLINE_WORDS, PROCEED_WORDS } from './correction-question.js';
import { genesisStateOf } from './create-initial-runtime-state.js';
import { clearQuestion, parkQuestion, type PendingQuestion } from './pending-question.js';
import { inputNamespaceCursor, stampNarrativeLines } from './transcript-cursor.js';

/** What a `manual`-mode `save` or `load` asks the host to do with the one slot. */
export type HostRequest = 'load' | 'save';

/** The parked confirm — the third question kind (D179). */
export type PendingConfirmQuestion = Extract<PendingQuestion, { kind: 'confirm' }>;

/** The answers a confirm accepts beside its own chip labels — a bare yes or no, however spelled. */


/** The input as the lexicon tables see it: folded, split, empties dropped. */
const tokensOf = (rawInput: string): readonly string[] =>
	foldCharacters(rawInput)
		.split(/\s+/u)
		.filter((token) => token !== '');

/**
 * The canonical intent this input spells WHOLE, or nothing.
 *
 * @remarks
 * Matched against the compiled lexicon rather than a literal list, so packs
 * that alias a directive get their own words for free. Exact-whole match
 * only: a single token checks `intentsByToken`, several check the multiword
 * aliases keyed on the first — the same two tables canonicalization reads,
 * consulted without a parse because a directive has no grammar. A directive
 * followed by anything (`save the key`) is not a directive; the parser
 * reads it and narrowing refuses it as it would any verb with no action.
 *
 * @param bundle - The loaded bundle
 * @param rawInput - Exactly what the player typed
 * @returns The canonical token of the intent the whole input names, or undefined
 */
export const wholeInputIntentOf = (bundle: EngineGameBundle, rawInput: string): string | undefined => {
	const inputTokens = tokensOf(rawInput);

	if (inputTokens.length === 0) {
		return undefined;
	}

	const leadingToken = inputTokens.slice(0, 1).join('');

	if (inputTokens.length === 1) {
		return bundle.compiledLexicon.intentsByToken.get(leadingToken)?.canonicalToken;
	}

	const candidateAliases = bundle.compiledLexicon.multiwordAliasesByFirstToken.get(leadingToken) ?? [];
	const matched = candidateAliases.find(
		(alias) =>
			alias.target.kind === 'intent' &&
			alias.tokenSequence.length === inputTokens.length &&
			alias.tokenSequence.every((aliasToken, tokenIndex) => aliasToken === inputTokens[tokenIndex]),
	);

	return matched?.target.kind === 'intent' ? matched.target.token : undefined;
};

/**
 * Which chrome directive this input is, whole — and the world still open to
 * hear it. The R11 terminal gate outranks every directive but one: a closed
 * world's inputs fall through to the ordinary pipeline, whose gate declines
 * them after the echo commit — except `restart` (D229, E16), which is what
 * an ending leaves to be done.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input (read for the terminal gate)
 * @param rawInput - Exactly what the player typed
 * @returns The directive, or undefined when the input is anything else
 */
export const chromeDirectiveOf = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): ChromeDirective | undefined => {
	const canonicalToken = wholeInputIntentOf(bundle, rawInput);
	const directives = Object.keys(CHROME_DIRECTIVE_TOKENS) as readonly ChromeDirective[];
	const directive = directives.find((candidate) => CHROME_DIRECTIVE_TOKENS[candidate] === canonicalToken);

	return directive !== 'restart' && terminalEndingOf(bundle, state) !== undefined ? undefined : directive;
};

/**
 * Commit a chrome answer: the echo, the history, the lines — no turn.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed (echoed verbatim, D23)
 * @param spoken - The slot responses to say, in order
 * @param context - The transcript context the lines stamp under
 * @param finish - The last word on the state, after the lines are in it
 * @returns The committed state and its new entries
 */
const commitChromeAnswer = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	spoken: readonly SpokenResponse[],
	context: string,
	finish: (state: RuntimeState) => RuntimeState,
): { readonly nextState: RuntimeState; readonly transcriptEntries: readonly RuntimeTranscriptEntry[] } => {
	const inputOrdinal = state.session.commandHistory.length + 1;
	const echo = stampNarrativeLines(
		bundle,
		state,
		inputNamespaceCursor(inputOrdinal, 0, state),
		[{ priority: 'standard', source: 'player', text: rawInput }],
		'player_input',
	);
	const stamped = stampNarrativeLines(bundle, state, echo.cursor, spoken.map((response) => response.line), context);
	const transcriptEntries: readonly RuntimeTranscriptEntry[] = [...echo.entries, ...stamped.entries];

	return {
		nextState: finish({
			...state,
			session: {
				...state.session,
				commandHistory: [...state.session.commandHistory, rawInput],
				responseUsage: bumpResponseUsage(state.session.responseUsage, spoken.map((response) => response.slot)),
				transcript: [...state.session.transcript, ...transcriptEntries],
			},
		}),
		transcriptEntries,
	};
};

/** A chrome answer as the reaction arm: no turn consumed, no world change. */
const asReaction = (
	committed: { readonly nextState: RuntimeState; readonly transcriptEntries: readonly RuntimeTranscriptEntry[] },
	hostRequest?: HostRequest,
): PlayerInputOutcome => ({
	corrections: [],
	...(hostRequest === undefined ? {} : { hostRequest }),
	nextState: committed.nextState,
	transcriptEntries: committed.transcriptEntries,
	type: 'reaction',
});

/**
 * `restart`, typed: ask first. The confirm is parked as a question, so the
 * chips at the input line ARE the buttons and the words they submit are the
 * player's own in the transcript (the R17 rule for every chip).
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed
 * @returns The committed ask, with the confirm parked
 */
export const askToRestart = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): PlayerInputOutcome => {
	const count = String(state.progression.visitedRoomIds.length);
	// D229 (E16): the title screen's door and the typed directive are one act
	// (D179), so the words a pack wrote for the door are the words typed
	// `restart` speaks, unless the pack claimed the slot itself.
	const doorWords = bundle.contentPack.manifest.presentation?.titleScreen?.restart;
	const confirm = speakSlot(bundle, state, 'restartConfirm', { count }, doorWords?.confirm);
	const proceed = speakSlot(bundle, state, 'restartProceedLabel', {}, doorWords?.proceedLabel);
	const keep = speakSlot(bundle, state, 'restartKeepLabel', {}, doorWords?.keepLabel);
	// The labels are slots so a pack can re-voice them; resolved here, since a
	// label is read as a chip and never stamped into the transcript. One line
	// each, joined rather than indexed, so no phantom "missing line" branch.
	const labelOf = (spoken: SpokenResponse): string => resolveNarrativeLines([spoken.line], bundle, state).map((line) => line.text).join('');
	const proceedLabel = labelOf(proceed);
	const keepLabel = labelOf(keep);
	// D229 (E16): typed, the confirm says how it is answered — the chips are
	// not on a terminal — with the labels lowered into the sentence.
	const answers = speakSlot(bundle, state, 'restartAnswers', { keepLabel: keepLabel.toLowerCase(), proceedLabel: proceedLabel.toLowerCase() });
	const inputOrdinal = state.session.commandHistory.length + 1;
	const context = `restart_confirm:${String(inputOrdinal)}`;
	const committed = commitChromeAnswer(bundle, state, rawInput, [confirm, answers], context, (next) => {
		const question: PendingConfirmQuestion = {
			act: 'restart',
			askedAtInputOrdinal: inputOrdinal,
			clauseText: foldCharacters(rawInput),
			decline: { label: keepLabel, words: [foldCharacters(keepLabel), ...DECLINE_WORDS]},
			kind: 'confirm',
			proceed: { label: proceedLabel, words: [foldCharacters(proceedLabel), ...PROCEED_WORDS]},
			// The SPOKEN text, off the transcript, so the surface shows exactly what was said (the D80 rule).
			questionText: next.session.transcript.filter((entry) => entry.triggerContext === context).map((entry) => entry.text).join(' '),
			roomId: state.progression.activeRoomId,
		};

		return parkQuestion(
			{ ...next, session: { ...next.session, responseUsage: bumpResponseUsage(next.session.responseUsage, [proceed.slot, keep.slot]) }},
			question,
		);
	});

	return asReaction(committed);
};

/** Whether the input, folded, is one of the words this side of a confirm accepts. */
const meansAnswer = (rawInput: string, words: readonly string[]): boolean => words.includes(tokensOf(rawInput).join(' '));

/**
 * The player's answer to a parked confirm, or nothing when the input is
 * neither side of it — in which case the caller closes the gate silently
 * and reads the input as an ordinary one.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input, with the confirm parked
 * @param rawInput - Exactly what the player typed (echoed)
 * @param words - The words being read as the answer (D226: an earlier input's, under `again`)
 * @param question - The parked confirm
 * @returns The outcome of answering, or undefined for "not an answer"
 */
export const answerConfirm = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	words: string,
	question: PendingConfirmQuestion,
	runAs: (cleared: RuntimeState, parseText: string) => PlayerInputOutcome,
): PlayerInputOutcome | undefined => {
	// D212: the corrected clause runs as if typed — through the whole
	// pipeline, with the question cleared first so it cannot answer itself —
	// while the echo and the history keep the player's own word ("yes").
	// Declined, the terminal says so and nothing is tried.
	if (question.act === 'run_correction') {
		if (meansAnswer(words, question.proceed.words)) {
			return runAs(clearQuestion(state), question.correctedClauseText);
		}

		if (meansAnswer(words, question.decline.words)) {
			const kept = speakSlot(bundle, state, 'correctionKept');

			return asReaction(commitChromeAnswer(bundle, state, rawInput, [kept], `correction_kept:${String(question.askedAtInputOrdinal)}`, clearQuestion));
		}

		return undefined;
	}

	if (meansAnswer(words, question.proceed.words)) {
		// Through the gate: the descent begins again, on the seed it was
		// running on — genesis is the whole of the next state, the transcript
		// with it. The answer is not echoed: nothing of the old session is
		// kept, and an echo of the words that ended it would be the one thing
		// that was.
		// D241: the next run begins on the Record this one leaves — what it was handed, and what it wrote.
		const nextState = genesisStateOf(bundle, state.randomness.sessionSeed, nextRecordOf(state));

		return {
			corrections: [],
			result: { firedEventIds: [], nextState, transcriptEntries: nextState.session.transcript },
			type: 'executed',
		};
	}

	if (meansAnswer(words, question.decline.words)) {
		const kept = speakSlot(bundle, state, 'restartKept');

		return asReaction(commitChromeAnswer(bundle, state, rawInput, [kept], `restart_kept:${String(question.askedAtInputOrdinal)}`, clearQuestion));
	}

	return undefined;
};

/**
 * Which slot answers `save` and `load` under each effective save mode (D179;
 * D231 added the host's `none`). Exhaustive over both, so a new mode or a
 * new directive is a compile error here before it is a silent slot.
 */
const SAVE_DIRECTIVE_SLOTS: Readonly<Record<EffectiveSaveMode, Readonly<Record<HostRequest, DefaultResponseSlot>>>> = {
	automatic: { load: 'loadAutomatic', save: 'saveAutomatic' },
	manual: { load: 'loadRestored', save: 'saveWritten' },
	none: { load: 'loadNothingKept', save: 'saveNothingKept' },
};

/**
 * `save` or `load`, typed: what it does depends on the pack's mode — and on
 * the host's declaration (D231).
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed
 * @param directive - Which of the two
 * @returns The committed answer — carrying the host's task on a `manual` pack
 */
export const answerSaveOrLoad = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	directive: HostRequest,
): PlayerInputOutcome => {
	// D231: the host's word outranks the pack's mode — a host that keeps
	// nothing answers both directives with that fact, and hands the host no
	// task, because there is nothing it could do with one.
	const saveMode = effectiveSaveModeOf(bundle);
	const slot = SAVE_DIRECTIVE_SLOTS[saveMode][directive];
	const spoken = speakSlot(bundle, state, slot);
	const committed = commitChromeAnswer(bundle, state, rawInput, [spoken], `${directive}:${slot}`, (next) => next);

	return asReaction(committed, saveMode === 'manual' ? directive : undefined);
};

/**
 * D185: the host's re-answer when LOAD found nothing to bring back. The
 * engine answered `loadRestored` without knowing whether a slot exists —
 * only the host does — so the host, having looked, commits THIS answer to
 * the same input instead: the player's `load` echoed once, the honest line
 * after it, no host task attached.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE the input — the one the engine answered from
 * @param rawInput - Exactly what the player typed
 * @returns The committed answer, a plain reaction
 */
export const answerLoadFoundNothing = (bundle: EngineGameBundle, state: RuntimeState, rawInput: string): PlayerInputOutcome => {
	const spoken = speakSlot(bundle, state, 'loadNothingSaved');
	const committed = commitChromeAnswer(bundle, state, rawInput, [spoken], 'load:loadNothingSaved', (next) => next);

	return asReaction(committed, undefined);
};

/**
 * The words `again` says again (D226, E12): the most recent input that was
 * not itself an `again`, or nothing when the session has said nothing else.
 *
 * @remarks
 * Read from `commandHistory`, which keeps what the player TYPED (D23), so
 * `again` after `again` reaches back past both to the words, and a replay
 * of `(bundle, seed, commands)` re-derives the same repetition. Blank inputs
 * are history too (D09) and are skipped: there is nothing in them to say.
 *
 * @param bundle - The loaded bundle (whose vocabulary says which words mean `again`)
 * @param state - The state BEFORE this input
 * @returns The words to repeat, or `undefined`
 */
const wordsToRepeat = (bundle: EngineGameBundle, state: RuntimeState): string | undefined =>
	[...state.session.commandHistory]
		.reverse()
		.find((earlierInput) => earlierInput.trim() !== '' && wholeInputIntentOf(bundle, earlierInput) !== CHROME_DIRECTIVE_TOKENS.again);

/**
 * `again`, typed: the last words said again through the whole conversation,
 * or the one line for a session with nothing to repeat.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed (`again`, `g`, `repeat` — echoed verbatim)
 * @param sayAgain - The whole conversation over other words, the typed input echoed
 * @returns The committed outcome
 */
const answerAgain = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	sayAgain: (words: string) => PlayerInputOutcome,
): PlayerInputOutcome => {
	const words = wordsToRepeat(bundle, state);

	if (words === undefined) {
		const nothing = speakSlot(bundle, state, 'nothingToRepeat');

		return asReaction(commitChromeAnswer(bundle, state, rawInput, [nothing], 'again:nothingToRepeat', (next) => next));
	}

	return sayAgain(words);
};

/**
 * The conversation wrapper's one call: a parked confirm's answer, or a chrome
 * directive, or nothing — in which case the input is the parser's.
 *
 * @remarks
 * A confirm is a gate, not a conversation. Its answer is read before
 * anything else; anything that is not an answer closes it silently and is
 * then read as the ordinary input it is — so a "yes" typed three commands
 * later can never fire the act it was asked about. The re-read goes back
 * through the caller, so the input meets every gate a fresh input meets.
 *
 * D226 (E12): the chrome reads the WORDS and echoes the TYPED input — the
 * two differ only under `again`, whose words are an earlier input's.
 *
 * @param bundle - The loaded bundle
 * @param state - The state BEFORE this input
 * @param rawInput - Exactly what the player typed
 * @param words - The words being read — the typed input, or the earlier input `again` repeats
 * @param readAgain - The whole pipeline, for the input that closed a confirm without answering it
 * @param runAs - The whole pipeline over another text (D212: the corrected clause a "yes" accepts), the typed input echoed
 * @param sayAgain - The whole CONVERSATION over other words (D226: what `again` repeats), the typed input echoed
 * @returns The outcome, when the chrome had the input; undefined when the parser should
 */
export const readChrome = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	rawInput: string,
	words: string,
	readAgain: (cleared: RuntimeState) => PlayerInputOutcome,
	runAs: (cleared: RuntimeState, parseText: string) => PlayerInputOutcome,
	sayAgain: (repeatedWords: string) => PlayerInputOutcome,
): PlayerInputOutcome | undefined => {
	const parked = state.session.pendingQuestion;

	if (parked?.kind === 'confirm') {
		return answerConfirm(bundle, state, rawInput, words, parked, runAs) ?? readAgain(clearQuestion(state));
	}

	const directive = chromeDirectiveOf(bundle, state, words);

	if (directive === 'again') {
		return answerAgain(bundle, state, rawInput, sayAgain);
	}

	if (directive === 'restart') {
		return askToRestart(bundle, state, rawInput);
	}

	return directive === undefined ? undefined : answerSaveOrLoad(bundle, state, rawInput, directive);
};
